/**
 * Minimal print / summary URL detection + CSS injection (no handout / QR deps).
 * Imported by drawer, notes, keyboard, and reveal-print-standalone so bundles stay small.
 */

function isSummaryOrPrintPdfQuery(search: string): boolean {
  const p = new URLSearchParams(search);
  return p.has('print-pdf') || p.has('summary');
}

/** True when the deck is in summary / print-handout mode (`summary` or `print-pdf` query). */
export function isRevealPrintPdfMode(): boolean {
  try {
    return isSummaryOrPrintPdfQuery(window.location.search || '');
  } catch {
    return false;
  }
}

/**
 * If the URL uses `?summary` without `print-pdf`, append `print-pdf=1` via replaceState so
 * Reveal's built-in PDF export runs. Call before Reveal.initialize (e.g. from reveal-print.js).
 */
export function ensureRevealSummaryPrintPdfQuery(): void {
  if (typeof window === 'undefined') return;
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.has('summary') && !p.has('print-pdf')) {
      p.set('print-pdf', '1');
      const u = new URL(window.location.href);
      u.search = p.toString();
      window.history.replaceState({}, '', u.toString());
    }
  } catch {
    /* ignore */
  }
}

function appendStylesheetOnce(href: string): void {
  if (!href) return;
  let resolved: string;
  try {
    resolved = new URL(href, window.location.href).href;
  } catch {
    resolved = href;
  }
  const links = document.querySelectorAll('link[rel="stylesheet"]');
  for (let i = 0; i < links.length; i++) {
    const el = links[i] as HTMLLinkElement;
    if (el.href === resolved) return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Inject Reveal PDF + Lectura print overrides. Uses absolute /css/ paths from site root.
 */
export function injectRevealPrintPdfStyles(): void {
  if (!isRevealPrintPdfMode()) return;
  const base = new URL('/css/', window.location.origin).href;
  appendStylesheetOnce(new URL('reveal-print-pdf.css', base).href);
  appendStylesheetOnce(new URL('reveal-print-slides-notes.css', base).href);
}
